import { newTierDraft, validateTiers, tierGaps, type TierDraft } from '../../lib/pricingTiers'

// Reusable tiered-price editor ("阶梯价"): rows of From / To / Price, To blank = open-ended top
// tier. Controlled and storage-agnostic -- the parent owns the TierDraft[] and persists it with
// saveTiers() from lib/pricingTiers.ts, so the same component serves supplier_rate_tiers today
// and pricing_rate_tiers (customer side) later with no changes.

const cellCls = 'border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60'

interface Props {
  tiers: TierDraft[]
  onChange: (tiers: TierDraft[]) => void
  readOnly?: boolean
  unitLabel?: string // e.g. "per_kg" -- shown in the header so the From/To numbers have a meaning
  currency?: string
}

export default function TierEditor({ tiers, onChange, readOnly = false, unitLabel, currency }: Props) {
  const sorted = [...tiers].sort((a, b) => a.tier_from - b.tier_from)
  const error = validateTiers(tiers)
  const gaps = tierGaps(tiers)

  const update = (key: string, patch: Partial<TierDraft>) =>
    onChange(tiers.map(t => t._key === key ? { ...t, ...patch } : t))

  const add = () => {
    const last = sorted[sorted.length - 1]
    // Adding after an open-ended tier: close that one at its own start + 1 would be a guess, so
    // close it at the new tier's start instead -- the new row starts where the old one now ends.
    if (last && last.tier_to === null) {
      const closeAt = last.tier_from + 1
      onChange([...tiers.map(t => t._key === last._key ? { ...t, tier_to: closeAt } : t), newTierDraft({ ...last, tier_to: closeAt })])
    } else {
      onChange([...tiers, newTierDraft(last)])
    }
  }

  const num = (v: string) => v === '' ? NaN : parseFloat(v)

  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        Quantity bands{unitLabel ? ` (${unitLabel})` : ''} — "To" blank = no upper limit.
      </div>
      {sorted.length > 0 && (
        <table className="text-xs">
          <thead>
            <tr className="text-left text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <th className="pr-2 pb-1">From</th><th className="pr-2 pb-1">To</th><th className="pr-2 pb-1">Price{currency ? ` (${currency})` : ''}</th><th />
            </tr>
          </thead>
          <tbody>
            {sorted.map(t => (
              <tr key={t._key}>
                <td className="pr-2 py-0.5"><input type="number" step="any" min="0" className={cellCls + ' w-20'} disabled={readOnly}
                  value={Number.isNaN(t.tier_from) ? '' : t.tier_from} onChange={e => update(t._key, { tier_from: num(e.target.value) })} /></td>
                <td className="pr-2 py-0.5"><input type="number" step="any" min="0" className={cellCls + ' w-20'} disabled={readOnly} placeholder="∞"
                  value={t.tier_to ?? ''} onChange={e => update(t._key, { tier_to: e.target.value === '' ? null : num(e.target.value) })} /></td>
                <td className="pr-2 py-0.5"><input type="number" step="0.01" min="0" className={cellCls + ' w-24'} disabled={readOnly}
                  value={Number.isNaN(t.unit_price) ? '' : t.unit_price} onChange={e => update(t._key, { unit_price: num(e.target.value) })} /></td>
                <td className="py-0.5">{!readOnly && (
                  <button type="button" onClick={() => onChange(tiers.filter(x => x._key !== t._key))} className="text-red-500 hover:underline">Remove</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {sorted.length === 0 && <div className="text-xs text-gray-400 mb-1">No tiers — the line's flat Unit Price applies at every quantity.</div>}
      {!readOnly && <button type="button" onClick={add} className="mt-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">+ Add tier</button>}
      {error && <div className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</div>}
      {!error && gaps.length > 0 && <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">No tier covers: {gaps.join(', ')}.</div>}
    </div>
  )
}
